/**
 * URL-as-state for conversations.
 *
 * useSession has read ?session= since the beginning (deep links, support
 * handoffs); these helpers add the WRITE side so the URL tracks the active
 * conversation — refresh and shared links land on the conversation you
 * were actually in, instead of whatever localStorage held.
 */

const CHAT_ROUTES = /^\/(mainchat|tools|study-design|workspace)(\/|$)/

/** Only chat-bearing workspace routes carry the session param. */
export function shouldReflectSession(pathname: string | null | undefined): boolean {
  return !!pathname && CHAT_ROUTES.test(pathname)
}

/**
 * The URL to write for sessionId, or null when the URL already reflects it
 * (callers skip the history call — no churn on every render).
 */
export function withSessionParam(href: string, sessionId: string): string | null {
  const url = new URL(href)
  if (url.searchParams.get('session') === sessionId) return null
  url.searchParams.set('session', sessionId)
  return url.toString()
}

/**
 * URL for a cross-workspace canvas handoff (handleOpenCanvas). CANVAS_META hrefs are
 * always bare tool paths with no existing query string, so this is a plain append —
 * not a general URL parser like withSessionParam, which needs an absolute base.
 */
export function canvasHandoffHref(href: string, sessionId: string): string {
  return `${href}?session=${encodeURIComponent(sessionId)}`
}

/**
 * Is canvasHref (a CANVAS_META entry's canvas page) the page we're already
 * on? True for tools whose canvasHref equals their workspaceHref (Study
 * Design, Test Cases — canvas is a tab within the same chat page); false
 * for every tool whose canvas is a genuinely separate route (Concierge:
 * /mainchat vs /mainchat/canvas; same split for Val, Norma, Marq).
 *
 * handleOpenCanvas used to compare item.targetTool !== currentMode ("cross
 * PERSONA") instead of this ("cross PAGE") — those happen to be the same
 * question for Study Design/Test Cases (one page, one persona) but are
 * wrong for every split-page tool: clicking a Concierge-tagged canvas item
 * while sitting on plain /mainchat skipped navigation entirely and just
 * dispatched switchToCanvasTab into a page that never mounts ToolCanvas —
 * a genuine no-op. Confirmed live 2026-07-20 impersonating an affected
 * user: the canvas list loaded fine, the click fired, and nothing after it
 * ever happened.
 */
export function isAlreadyOnCanvasPage(pathname: string, canvasHref: string): boolean {
  return pathname === canvasHref || pathname.startsWith(`${canvasHref}/`)
}
